import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import PageHeader from "@/components/common/PageHeader";
import { tenantService } from "@/services/tenant.service";
import type { TenantListItem } from "@/types";
import StatusSelect from "@/pages/tenants/components/StatusSelect";
import { usePermissions } from "@/hooks/usePermissions";
import { adminWorkspaceService } from "@/services/admin-workspace.service";
import toast from "react-hot-toast";

function StatusBadge({ status }: { status: string }) {
  const style =
    status === "active"
      ? "bg-emerald-50 text-emerald-700"
      : status === "suspended"
        ? "bg-amber-50 text-amber-700"
        : "bg-slate-100 text-slate-600";

  return (
    <span className={`rounded-full px-2 py-1 text-xs font-semibold ${style}`}>
      {status}
    </span>
  );
}

function TenantRow({
  tenant,
  onStatusChange,
  canManage,
}: {
  tenant: TenantListItem;
  onStatusChange: (tenantId: string, status: string, reason: string) => void;
  canManage: boolean;
}) {
  const isActive = tenant.status === "active";
  return (
    <tr className="border-b border-slate-100 last:border-none">
      <td className="px-4 py-3 text-sm font-semibold text-slate-900">
        <Link to={`/tenants/${tenant.id}`} className="hover:text-slate-700">
          {tenant.name}
        </Link>
      </td>
      <td className="px-4 py-3 text-sm text-slate-600">{tenant.email}</td>
      <td className="px-4 py-3 text-sm text-slate-600">{tenant.city || "—"}</td>
      <td className="px-4 py-3 text-sm text-slate-600">
        {tenant._count?.stores ?? 0} stores
      </td>
      <td className="px-4 py-3 text-sm text-slate-600">
        {tenant._count?.users ?? 0} users
      </td>
      <td className="px-4 py-3 text-sm">
        <StatusBadge status={tenant.status} />
      </td>
      <td className="px-4 py-3 text-right text-xs text-slate-500">
        {new Date(tenant.createdAt).toLocaleDateString()}
      </td>
      {canManage && (
        <td className="px-4 py-3 text-right">
          <button
            type="button"
            onClick={() => {
              const nextStatus = isActive ? "suspended" : "active";
              const reason = window.prompt(
                `Enter the reason to ${isActive ? "suspend" : "activate"} this tenant (minimum 8 characters):`,
              );
              if (reason?.trim() && reason.trim().length >= 8) {
                onStatusChange(tenant.id, nextStatus, reason.trim());
              }
            }}
            className="rounded-lg border border-slate-200 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-600 transition hover:bg-slate-50"
          >
            {isActive ? "Suspend" : "Activate"}
          </button>
        </td>
      )}
    </tr>
  );
}

function TenantListPage() {
  const { canManageTenants, canOnboardTenants } = usePermissions();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<string>("");
  const [savedViewId, setSavedViewId] = useState("");
  const [page, setPage] = useState(1);
  const limit = 10;

  const queryParams = useMemo(
    () => ({
      page,
      limit,
      search: search.trim() || undefined,
      status: status || undefined,
    }),
    [limit, page, search, status],
  );

  const queryClient = useQueryClient();
  const { data: savedViews = [] } = useQuery({
    queryKey: ["workspace-views", "TENANTS"],
    queryFn: () => adminWorkspaceService.savedViews("TENANTS"),
  });
  const { data, isLoading } = useQuery({
    queryKey: ["admin-tenants", queryParams],
    queryFn: () => tenantService.list(queryParams),
  });

  const { mutate: updateStatus, isPending } = useMutation({
    mutationFn: ({
      id,
      status,
      reason,
    }: {
      id: string;
      status: string;
      reason: string;
    }) => tenantService.update(id, { status, reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-tenants"] });
    },
  });

  const saveCurrentView = useMutation({
    mutationFn: (name: string) =>
      adminWorkspaceService.createSavedView({
        resourceType: "TENANTS",
        name,
        filters: {
          ...(search.trim() ? { search: search.trim() } : {}),
          ...(status ? { status } : {}),
        },
        columns: ["name", "email", "city", "stores", "users", "status"],
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workspace-views"] });
      toast.success("Tenant view saved");
    },
  });

  const applySavedView = (id: string) => {
    setSavedViewId(id);
    const view = savedViews.find((item: any) => item.id === id);
    if (!view) return;
    const filters = (view.filters ?? {}) as Record<string, unknown>;
    setSearch(typeof filters.search === "string" ? filters.search : "");
    setStatus(typeof filters.status === "string" ? filters.status : "");
    setPage(1);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Tenants"
        subtitle="Monitor pharmacies, onboarding, and account status."
        actions={
          canOnboardTenants ? (
            <Link
              to="/tenants/onboarding"
              className="rounded-xl bg-slate-900 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white transition hover:bg-slate-800"
            >
              New tenant
            </Link>
          ) : undefined
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <input
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
          placeholder="Search tenants by name, email, GST, or phone"
          className="h-10 flex-1 rounded-xl border border-slate-200 px-4 text-sm focus:border-slate-400 focus:outline-none"
        />
        <StatusSelect
          value={status}
          includeAll
          onChange={(value) => {
            setStatus(value);
            setPage(1);
          }}
        />
        <select
          aria-label="Saved tenant view"
          value={savedViewId}
          onChange={(event) => applySavedView(event.target.value)}
          className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-600"
        >
          <option value="">Saved views</option>
          {savedViews.map((view: any) => (
            <option key={view.id} value={view.id}>
              {view.name}
              {view.isDefault ? " · default" : ""}
              {view.isShared ? " · shared" : ""}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => {
            const name = window.prompt("Name this tenant view")?.trim();
            if (name) saveCurrentView.mutate(name);
          }}
          className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-xs font-bold text-slate-600 hover:bg-slate-50"
        >
          Save current view
        </button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-left">
          <thead className="bg-slate-50 text-xs uppercase tracking-[0.2em] text-slate-400">
            <tr>
              <th className="px-4 py-3">Tenant</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">City</th>
              <th className="px-4 py-3">Stores</th>
              <th className="px-4 py-3">Users</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Created</th>
              {canManageTenants && (
                <th className="px-4 py-3 text-right">Action</th>
              )}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td
                  className="px-4 py-6 text-sm text-slate-500"
                  colSpan={canManageTenants ? 8 : 7}
                >
                  Loading tenants...
                </td>
              </tr>
            ) : data?.data.length ? (
              data.data.map((tenant) => (
                <TenantRow
                  key={tenant.id}
                  tenant={tenant}
                  canManage={canManageTenants}
                  onStatusChange={(id, status, reason) => {
                    if (isPending) {
                      return;
                    }
                    updateStatus({ id, status, reason });
                  }}
                />
              ))
            ) : (
              <tr>
                <td
                  className="px-4 py-6 text-sm text-slate-500"
                  colSpan={canManageTenants ? 8 : 7}
                >
                  No tenants found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-slate-500">
        <span>
          Page {data?.meta.page ?? page} of {data?.meta.totalPages ?? 1}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((value) => Math.max(1, value - 1))}
            className="rounded-lg border border-slate-200 px-3 py-1 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Previous
          </button>
          <button
            type="button"
            disabled={Boolean(data) && page >= (data?.meta.totalPages ?? 1)}
            onClick={() => setPage((value) => value + 1)}
            className="rounded-lg border border-slate-200 px-3 py-1 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

export default TenantListPage;
